/**
 * Leave Display Formatting Utilities
 *
 * Handles rounding and formatting for leave/holiday displays.
 * Display is always rounded to 0.5 steps with German locale formatting.
 */

/**
 * Rounds a number to the nearest 0.5 (half day).
 * Examples: 12.3 → 12.5, 12.7 → 13.0, 12.25 → 12.5
 */
export function roundToHalf(n: number): number {
  return Math.round(n * 2) / 2;
}

/**
 * Formats days with half-day rounding and German locale.
 * - Rounds to nearest 0.5
 * - Uses German decimal separator (12,5 not 12.5)
 * - Shows 0 decimals for whole numbers, 1 decimal for halves
 *
 * Examples:
 *   formatDaysHalf(12.3)    → "12,5"
 *   formatDaysHalf(12.0)    → "12"
 *   formatDaysHalf("28.75") → "29"
 *   formatDaysHalf(null)    → "—"
 */
export function formatDaysHalf(
  value: number | string | null | undefined,
  locale = 'de-DE'
): string {
  if (value === null || value === undefined) return '—';

  // Handle empty string
  const trimmed = typeof value === 'string' ? value.trim() : value;
  if (trimmed === '') return '—';

  const num = typeof trimmed === 'string' ? Number(trimmed) : trimmed;
  if (Number.isNaN(num)) return '—';

  const half = roundToHalf(num);
  const decimals = half % 1 === 0 ? 0 : 1; // 0 for whole numbers, 1 for halves

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(half);
}
